import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import React from 'react';

import { MatterSelectorCombobox } from '@/components/matter/MatterSelectorCombobox';
import { useReassignIntakeMutation } from '@/services/api/intakeService';

const getErrorMessage = (
  error: FetchBaseQueryError | SerializedError,
): string => {
  if ('data' in error && error.data && typeof error.data === 'object') {
    const data = error.data as {
      detail?: string;
      message?: string;
      error?: string;
    };
    return data.detail || data.message || data.error || 'An error occurred';
  }
  if ('message' in error && error.message) {
    return error.message;
  }
  return 'An error occurred';
};

interface ReassignIntakeModalProps {
  intakeId: string;
  currentMatterId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ReassignIntakeModal: React.FC<ReassignIntakeModalProps> = ({
  intakeId,
  currentMatterId,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [selectedMatterId, setSelectedMatterId] = React.useState('');
  const [error, setError] = React.useState('');
  const [reassignIntake, { isLoading }] = useReassignIntakeMutation();

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setSelectedMatterId('');
      setError('');
    }
  }, [isOpen]);

  const handleReassign = async () => {
    if (!selectedMatterId) return;

    try {
      setError('');
      await reassignIntake({
        intakeId,
        targetMatterId: selectedMatterId,
      }).unwrap();
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err as FetchBaseQueryError | SerializedError));
    }
  };

  const isSameAsCurrent = selectedMatterId === currentMatterId;
  const isInvalidSelection = !selectedMatterId || isSameAsCurrent;

  let selectionHint = '';
  if (isSameAsCurrent) {
    selectionHint = 'This intake is already assigned to this matter.';
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        aria-label="Close reassign intake modal"
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      <div
        className="relative mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reassign-intake-title"
        tabIndex={-1}
      >
        <div className="mb-6 flex items-center justify-between">
          <h2
            id="reassign-intake-title"
            className="text-lg font-semibold text-zinc-900"
          >
            Reassign Intake to Matter
          </h2>
          <button
            type="button"
            className="text-sm text-zinc-500 hover:text-zinc-700"
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </button>
        </div>

        <p className="text-sm text-zinc-600 mb-4">
          Select a different matter to assign this intake to. This will update
          the matter association for this intake.
        </p>

        <div className="space-y-4">
          {currentMatterId && (
            <div className="text-sm">
              <span className="font-medium text-zinc-700">Current matter:</span>
              <span className="ml-2 text-zinc-500">
                {currentMatterId.slice(0, 8)}...
              </span>
            </div>
          )}

          <div>
            <span className="text-sm font-medium text-zinc-900 mb-2 block">
              Select new matter
            </span>
            <MatterSelectorCombobox
              value={selectedMatterId}
              onChange={setSelectedMatterId}
              includeAllOption={false}
              aria-label="Select matter to reassign to"
              placeholder="Search matters by name or ID"
              isOptionDisabled={(m) => m.id === currentMatterId}
            />
            {selectionHint && (
              <div
                className="mt-2 text-sm text-zinc-500"
                aria-live="polite"
                role="status"
              >
                {selectionHint}
              </div>
            )}
            {error && (
              <div className="mt-2 text-sm text-red-600" role="alert">
                {error}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-md hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleReassign}
            disabled={isLoading || isInvalidSelection}
          >
            {isLoading ? 'Reassigning...' : 'Reassign'}
          </button>
        </div>
      </div>
    </div>
  );
};
